// Lightweight wrapper around the browser Notifications API.
// Falls back gracefully in environments without it (SSR, older browsers).

const isSupported = (): boolean =>
  typeof window !== "undefined" && "Notification" in window;

export function getPermission(): NotificationPermission | "unsupported" {
  if (!isSupported()) return "unsupported";
  return Notification.permission;
}

export async function requestPermission(): Promise<NotificationPermission | "unsupported"> {
  if (!isSupported()) return "unsupported";
  if (Notification.permission === "granted" || Notification.permission === "denied") {
    return Notification.permission;
  }
  try {
    return await Notification.requestPermission();
  } catch {
    return "denied";
  }
}

export interface BrowserNotificationOptions {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  onClick?: () => void;
  // If true, fires even when the tab is focused. Defaults to false
  // (typical UX: in-tab toast handles focused state, browser push is for blurred tabs).
  showWhileFocused?: boolean;
}

export function showBrowserNotification({
  title,
  body,
  icon,
  tag,
  onClick,
  showWhileFocused = false,
}: BrowserNotificationOptions): Notification | null {
  if (!isSupported()) return null;
  if (Notification.permission !== "granted") return null;
  if (!showWhileFocused && typeof document !== "undefined" && !document.hidden) {
    return null;
  }

  try {
    const notification = new Notification(title, {
      body,
      icon,
      tag,
      silent: false,
    });

    if (onClick) {
      notification.onclick = (event) => {
        event.preventDefault();
        try {
          window.focus();
        } catch {
          // some browsers block focus() unless from a user gesture
        }
        onClick();
        notification.close();
      };
    }

    return notification;
  } catch (error) {
    console.error("[browserNotifications] failed to show", error);
    return null;
  }
}
